//* Create a Create Quote API request
//* based on the applicant(s) details.

import { format } from "date-fns";
import { toUpperFirstCharacterFirstWord } from "../utilities/mappingHelper.js";
import { isAdditionalPolicyPresent } from "../utilities/preQualificationUtils.js";

const generalDetails = (inputData, additionalData, isCC) => {
	return {
		basicDetails: {
			residentialAddress: {
				state: inputData?.state?.toUpperCase(),
			},
			telephone: {
				work: null,
				mobile: inputData?.contactNumber.replace(/\s/g, ""),
			},
			email: inputData?.emailAddress.replace(/\s/g, ""),
			marketingOptOut: additionalData?.marketingOptOut,
		},
		discounts: {
			promotionalCode:
				inputData?.promoCode && additionalData?.promoCodeValid === true
					? inputData?.promoCode
					: "", //* Don't send a promo code unless it's valid.
		},
		declarations: {
			hasFollowUpCall: isCC ? null : true, //* we always want the hasFollowUpCall set to true for the online consent flow. Null otherwise.
		},
	};
};

const life = (inputData) => {
	return { sumInsured: inputData };
};

const unableToWorkBuild = (inputData) => {
	return { sumInsured: inputData };
};

const criticalIllnessBuild = (inputData) => {
	return { sumInsured: inputData };
};

const covers = (inputData, originalAuraOutcome) => {
	const unableToWorkBool = inputData?.permanentDisabilityCover ? true : false;
	const criticalIllnessBool =
		inputData?.illnessCover && inputData?.shouldUseIllnessCover ? true : false;

	const hasUnableToWorkDecline =
		originalAuraOutcome?.covers?.hasUnableToWorkDecline;
	const hasCriticalIllnessDecline =
		originalAuraOutcome?.covers?.hasCriticalIllnessDecline;

	const coverDetails = {
		life: life(inputData?.lifeCover),
		...(unableToWorkBool === true && {
			unableToWork: unableToWorkBuild(inputData?.permanentDisabilityCover),
		}),
		...(hasUnableToWorkDecline === true && {
			unableToWork: { auraStatus: "DCL" },
		}),
		...(criticalIllnessBool === true && {
			criticalIllness: criticalIllnessBuild(inputData?.illnessCover),
		}),
		...(hasCriticalIllnessDecline === true && {
			criticalIllness: { auraStatus: "DCL" },
		}),
	};

	//* If both cover and decline are false, remove the cover.
	// if (criticalIllnessBool === false && hasCriticalIllnessDecline === false) {
	// 	delete coverDetails.criticalIllness;
	// }

	// if (unableToWorkBool === false && hasUnableToWorkDecline === false) {
	// 	delete coverDetails.unableToWork;
	// }

	return {
		...coverDetails,
	};
};

const basicDetails = (inputData) => {
	return {
		title: toUpperFirstCharacterFirstWord(inputData?.title),
		firstName: toUpperFirstCharacterFirstWord(inputData?.firstName),
		surname: toUpperFirstCharacterFirstWord(inputData?.surname),
	};
};

const underwritingQuestions = (inputData) => {
	const otherInsurance =
		inputData?.otherAllianzInsuranceQuestion === "yes" ? true : false;
	const isOccupationCode =
		inputData?.permanentDisabilityCover && inputData?.occupationList
			? true
			: false;
	const isTraumaCover = inputData?.illnessCover ? true : false;

	return {
		hasSmokedLast12Months: inputData?.tobaccoQuestion === "yes" ? true : false,
		permanentResidentOfAustralia:
			inputData?.residentQuestion === "yes" ? true : false,
		acceptTermsAndConditions: inputData?.termsAndConditions,
		dateOfBirth: format(new Date(inputData?.dob), "yyyy-MM-dd"),
		gender: inputData?.gender === "male" ? "M" : "F",
		...(isTraumaCover === true && { traumaCoverExceedsLimit: false }),
		...(isOccupationCode === true && {
			occupationCode: inputData?.occupationList.slice(0, 2),
		}),
		hasOtherAllianzInsurance: otherInsurance,
		...(otherInsurance === true && {
			otherAllianzProducts: inputData.otherAllianzInsuranceList
				.toString()
				.toUpperCase(),
		}),
	};
};

const insuredLists = (inputData, originalAuraOutcome) => {
	return {
		basicDetails: basicDetails(inputData),
		covers: covers(inputData, originalAuraOutcome),
		underwritingQuestions: underwritingQuestions(inputData),
	};
};

//** Local state to create quite mapping */
export const createQuoteRequestMapping = async (
	inputData,
	originalAuraOutcome = null
) => {
	const additionalData = {
		promoCodeValid:
			// inputData?.promoCodeValid || inputData?.personOne?.promoCode?.length > 0
			inputData?.promoCodeValid && inputData?.personOne?.promoCode?.length > 0
				? true
				: false, //* we only add a promo code if it's valid.
		marketingOptOut: inputData?.optOutDirectMarketingQuestion,
	};

	const isCC = inputData.application === "online" ? false : true;

	const { numberOfApplicants, personOne, personTwo } = { ...inputData };
	const response = {};
	const secondApplicant = numberOfApplicants === 2 ? true : false;

	//* Only add the second applicant details, if there is a second applicant.
	let insuredList = [
		insuredLists(personOne, originalAuraOutcome),
		...(secondApplicant === true
			? [insuredLists(personTwo, originalAuraOutcome)]
			: []),
	];

	response.insuredList = insuredList;
	response.general = generalDetails(personOne, additionalData, isCC);
	response.updateUser = inputData.updateUser;
	// add additional policy holder object, only add the object if it contains value

	if (isAdditionalPolicyPresent(inputData)) {
		const additionalPolicyHolderDetails = {};
		additionalPolicyHolderDetails.title = toUpperFirstCharacterFirstWord(
			inputData.additionalPolicyHolderTitle
		);
		additionalPolicyHolderDetails.firstName = toUpperFirstCharacterFirstWord(
			inputData.additionalPolicyHolderFirstName
		);
		additionalPolicyHolderDetails.surname = toUpperFirstCharacterFirstWord(
			inputData.additionalPolicyHolderSurname
		);
		additionalPolicyHolderDetails.dateOfBirth = format(
			new Date(inputData.additionalPolicyHolderDOB),
			"yyyy-MM-dd"
		);
		response.general.additionalOwner = additionalPolicyHolderDetails;
	}
	return response;
};
